import { dependencyId, type Dependency, type Task, type TaskId } from '@bigint-project/core';
import { formatPredecessorList, parsePredecessorInput, type ParsedPredecessor } from './predecessor.js';

/**
 * 前置任務欄位的 ref ↔ TaskId 對應規則。
 *
 * **顯示一律用 WBS 碼**(例如 "1.2.3"、頂層則是 "3")——那是使用者在表格 WBS 欄
 * 直接看得到的編號,不需要再回頭數第幾列。
 *
 * 解析(使用者輸入)時的優先序:
 *
 * 1. 先比對 `task.wbs`。完全相符就用它,含不含點都一樣。
 * 2. 比不到 WBS、且 ref 是純數字時,退回舊的「列號」語意 ——
 *    **完整任務清單(props.tasks,已依 WBS 排序,不受目前收合狀態影響)**的第 N 筆
 *    (1-based),對應 MS Project 的 Task ID 欄位語意。保留這條退路是為了讓既有檔案、
 *    既有筆記與從 MS Project 貼過來的列號輸入仍然可用。
 *
 * 列號採「全部任務(未過濾收合)的序號」而非「目前可見列的序號」:摺疊一個摘要任務
 * 不該讓其他任務的編號跳號,否則同一個 "3" 在收合前後會指向不同任務。
 */
export function resolveTaskRef(ref: string, tasks: readonly Task[]): TaskId | null {
  const trimmed = ref.trim();
  if (trimmed === '') return null;

  const byWbs = tasks.find((task) => task.wbs === trimmed);
  if (byWbs !== undefined) return byWbs.id;

  // 含點的 ref 只可能是 WBS 碼;比不到就是找不到,不會誤解成列號。
  if (trimmed.includes('.')) return null;

  const rowNumber = Number(trimmed);
  if (!Number.isInteger(rowNumber) || rowNumber < 1 || rowNumber > tasks.length) return null;
  const task = tasks[rowNumber - 1];
  return task ? task.id : null;
}

/** 將 TaskId 格式化為顯示用的 ref:一律用 WBS 碼(WBS 為空時才退回列號)。 */
export function formatTaskRef(taskId: TaskId, tasks: readonly Task[]): string {
  const index = tasks.findIndex((task) => task.id === taskId);
  // 理論上不會發生(dependencies 應只指向存在的任務);保底顯示避免整格編輯失敗。
  if (index === -1) return '?';
  const wbs = tasks[index]?.wbs.trim() ?? '';
  return wbs === '' ? String(index + 1) : wbs;
}

/** 取得某任務(作為 successor)的完整前置依賴清單。 */
export function getTaskPredecessors(
  taskId: TaskId,
  dependencies: readonly Dependency[],
): readonly Dependency[] {
  return dependencies.filter((dependency) => dependency.successorId === taskId);
}

/** 將某任務的前置依賴格式化為顯示字串,例如 "1FS+2d,3SS-1d"。 */
export function formatTaskPredecessorsDisplay(
  taskId: TaskId,
  dependencies: readonly Dependency[],
  tasks: readonly Task[],
): string {
  const items: ParsedPredecessor[] = getTaskPredecessors(taskId, dependencies).map((dependency) => ({
    ref: formatTaskRef(dependency.predecessorId, tasks),
    type: dependency.type,
    lagMinutes: dependency.lagMinutes,
    lagIsElapsed: dependency.lagIsElapsed ?? false,
  }));
  return formatPredecessorList(items);
}

export interface BuildDependenciesSuccess {
  readonly ok: true;
  /** 該任務作為 successor 的「完整」新依賴清單(取代,不是差異)。 */
  readonly dependencies: readonly Dependency[];
}

export interface BuildDependenciesFailure {
  readonly ok: false;
  /** 給使用者看的錯誤訊息;呼叫端必須不落地此次編輯,並提示使用者修正。 */
  readonly error: string;
}

export type BuildDependenciesResult = BuildDependenciesSuccess | BuildDependenciesFailure;

/**
 * 把使用者輸入的前置任務字串解析並解析 ref 為 TaskId,組成新的 Dependency[]。
 *
 * 找不到 ref、自我參照、或會形成明顯雙節點循環(A→B 且 B→A)時一律回傳失敗,
 * 呼叫端(TaskGrid)必須保留編輯狀態、顯示錯誤,不可靜默吞掉或送出部分結果。
 * 更複雜的多節點循環不在此檢查,交由排程引擎的 CircularDependencyError 處理。
 */
export function buildDependenciesFromInput(
  text: string,
  taskId: TaskId,
  tasks: readonly Task[],
  existingDependencies: readonly Dependency[],
): BuildDependenciesResult {
  const parsed = parsePredecessorInput(text);
  if (parsed === null) {
    return { ok: false, error: `無法解析前置任務語法:「${text}」` };
  }

  const nextDependencies: Dependency[] = [];
  for (const item of parsed) {
    const predecessorId = resolveTaskRef(item.ref, tasks);
    if (predecessorId === null) {
      return {
        ok: false,
        error: `找不到前置任務「${item.ref}」(請輸入正確的 WBS 編號)`,
      };
    }
    if (predecessorId === taskId) {
      return { ok: false, error: '任務不能把自己設為前置任務' };
    }

    const wouldCreateDirectCycle = existingDependencies.some(
      (dependency) => dependency.predecessorId === taskId && dependency.successorId === predecessorId,
    );
    if (wouldCreateDirectCycle) {
      return {
        ok: false,
        error: `無法新增:「${item.ref}」已將本任務設為它的前置任務,會形成循環相依`,
      };
    }

    nextDependencies.push({
      id: dependencyId(`d-${predecessorId}-${taskId}`),
      predecessorId,
      successorId: taskId,
      type: item.type,
      lagMinutes: item.lagMinutes,
      ...(item.lagIsElapsed ? { lagIsElapsed: true } : {}),
    });
  }

  return { ok: true, dependencies: nextDependencies };
}
